import { useEffect, useRef, useCallback } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import { OPENWEATHERMAP_API_KEY } from './weatherConstants';
import { timelineEpochMs } from '../../../utils/gdacs/stormClock';
import { WEATHER_LAYERS, WEATHER_PALETTES } from '../home/WeatherMapControls';

const PRELOAD_CONFIGS = {
  mobile: {
    framesAhead: 3,
    framesBehind: 1,
    maxCachedAhead: 6,
    maxCachedBehind: 3,
    keepBuffer: 1,
  },
  tablet: {
    framesAhead: 4,
    framesBehind: 2,
    maxCachedAhead: 8,
    maxCachedBehind: 4,
    keepBuffer: 2,
  },
  desktop: {
    framesAhead: 6,
    framesBehind: 3,
    maxCachedAhead: 10,
    maxCachedBehind: 6,
    keepBuffer: 2,
  },
};

const getDeviceType = () => {
  if (typeof window === 'undefined') return 'desktop';
  const ua = navigator.userAgent.toLowerCase();
  const screenWidth = window.innerWidth;
  if (/iphone|ipod|android.*mobile|windows phone|blackberry/i.test(ua) || screenWidth < 768) return 'mobile';
  if (/ipad|android(?!.*mobile)|tablet/i.test(ua) || (screenWidth >= 768 && screenWidth < 1024)) return 'tablet';
  return 'desktop';
};

const PRELOAD_CONFIG = PRELOAD_CONFIGS[getDeviceType()];

/**
 * When the timeline is not playing, mount fewer adjacent frames so zoom/pan
 * triggers fewer concurrent OpenWeather tile requests. Full window while playing.
 */
const getPreloadWindow = (isPlaying) => {
  const c = PRELOAD_CONFIG;
  if (isPlaying) {
    return {
      framesAhead: c.framesAhead,
      framesBehind: c.framesBehind,
      maxCachedAhead: c.maxCachedAhead,
      maxCachedBehind: c.maxCachedBehind,
    };
  }
  return {
    framesAhead: Math.min(1, c.framesAhead),
    framesBehind: Math.min(1, c.framesBehind),
    maxCachedAhead: Math.min(3, c.maxCachedAhead),
    maxCachedBehind: Math.min(2, c.maxCachedBehind),
  };
};

const AnimatedWeatherLayer = ({
  activeLayer, currentHour, startDate, apiMode, isDark, opacity = 0.8,
  visible = true, isPlaying = false,
  // Caller-supplied forecast horizon in hours from `startDate`. When omitted
  // we keep the legacy 240h default so existing callers behave the same.
  maxHours = 240,
  onZoomStart, onTilesLoaded, onLayerLoading, onLayerReady,
  onTilesLoadingChange,
}) => {
  const map = useMap();
  const layersRef = useRef({});
  const loadStateRef = useRef({});
  const visibleKeyRef = useRef(null);
  const targetKeyRef = useRef(null);
  const prevLayerRef = useRef(null);
  const prevThemeRef = useRef(null);
  const isFirstFrameLoadedRef = useRef(true);
  const preloadTimeoutRef = useRef(null);
  const waitingForTilesAfterZoomRef = useRef(false);
  const layerConfig = WEATHER_LAYERS.find((l) => l.id === activeLayer) || WEATHER_LAYERS[0];

  const themeKey = isDark ? 'dark' : 'light';
  const palette = WEATHER_PALETTES?.[themeKey]?.[activeLayer];
  // Ref updated synchronously in render so tile-load callbacks always see current value
  const visibleRef = useRef(visible);
  const prevVisible = visibleRef.current;
  visibleRef.current = visible;
  const visibleLayerEventsRef = useRef({ key: null, onLoading: null, onLoad: null });

  // Hide/show all layers via opacity when `visible` prop changes
  useEffect(() => {
    if (!visible) {
      Object.values(layersRef.current).forEach((layer) => layer.setOpacity(0));
    } else if (!prevVisible && visible && visibleKeyRef.current) {
      const currentLayer = layersRef.current[visibleKeyRef.current];
      if (currentLayer) currentLayer.setOpacity(opacity);
    }
  }, [visible, opacity]);

  const buildUrl = useCallback((unixTime) => {
    if (apiMode === 'paid') {
      let url = `https://maps.openweathermap.org/maps/2.0/weather/${layerConfig.layerCodePaid}/{z}/{x}/{y}?date=${unixTime}&appid=${OPENWEATHERMAP_API_KEY}&opacity=${opacity}`;
      if (palette) url += `&palette=${encodeURIComponent(palette)}`;
      return url;
    }
    return `https://tile.openweathermap.org/map/${layerConfig.layerCode}/{z}/{x}/{y}.png?appid=${OPENWEATHERMAP_API_KEY}`;
  }, [apiMode, layerConfig, palette, opacity]);

  const getCurrentUnixTime = useCallback(() => {
    const unixTime = Math.floor(timelineEpochMs(startDate, currentHour) / 1000);
    return Math.floor(unixTime / 10800) * 10800;
  }, [startDate, currentHour]);

  const getLayerKey = useCallback((unixTime) => `${activeLayer}-${themeKey}-${unixTime}`, [activeLayer, themeKey]);

  const isLayerReady = useCallback((key) => {
    if (!key) return false;
    if (loadStateRef.current[key] === 'loaded') return true;
    const layer = layersRef.current[key];
    // Only consider Leaflet's loading state authoritative when the layer is mounted.
    // Detached tile layers report as "not loading" even though they haven't fetched tiles yet.
    if (layer && map?.hasLayer(layer) && typeof layer.isLoading === 'function' && !layer.isLoading()) {
      loadStateRef.current[key] = 'loaded';
      return true;
    }
    return false;
  }, [map]);

  const ensureOnMap = useCallback((key) => {
    if (!key) return;
    const layer = layersRef.current[key];
    if (!layer || !map) return;
    if (!map.hasLayer(layer)) {
      if (loadStateRef.current[key] !== 'loaded') loadStateRef.current[key] = 'loading';
      layer.addTo(map);
    }
  }, [map]);

  const removeFromMap = useCallback((key) => {
    if (!key) return;
    const layer = layersRef.current[key];
    if (!layer || !map) return;
    if (map.hasLayer(layer)) map.removeLayer(layer);
  }, [map]);

  const bindVisibleLayerEvents = useCallback((key) => {
    if (!key) return;
    const layer = layersRef.current[key];
    if (!layer) return;

    const prev = visibleLayerEventsRef.current;
    if (prev.key && prev.key !== key) {
      const prevLayer = layersRef.current[prev.key];
      if (prevLayer && prev.onLoading && prev.onLoad) {
        prevLayer.off('loading', prev.onLoading);
        prevLayer.off('load', prev.onLoad);
      }
    }
    if (prev.key === key) return;

    const onLoading = () => {
      if (typeof onTilesLoadingChange === 'function') onTilesLoadingChange(true);
    };
    const onLoad = () => {
      if (typeof onTilesLoadingChange === 'function') onTilesLoadingChange(false);
    };
    layer.on('loading', onLoading);
    layer.on('load', onLoad);
    visibleLayerEventsRef.current = { key, onLoading, onLoad };

    if (typeof onTilesLoadingChange === 'function') {
      const currentlyLoading = typeof layer.isLoading === 'function' ? layer.isLoading() : false;
      onTilesLoadingChange(!!currentlyLoading);
    }
  }, [onTilesLoadingChange]);

  const resolveZoomWaitForLayer = useCallback((layer) => {
    if (!waitingForTilesAfterZoomRef.current) return;
    if (!layer) return;

    const finish = () => {
      if (!waitingForTilesAfterZoomRef.current) return;
      waitingForTilesAfterZoomRef.current = false;
      if (typeof onTilesLoadingChange === 'function') onTilesLoadingChange(false);
      if (onTilesLoaded) onTilesLoaded();
    };

    if (typeof layer.isLoading === 'function' && !layer.isLoading()) finish();
    else layer.once('load', finish);
  }, [onTilesLoaded, onTilesLoadingChange]);

  const switchVisibleFrame = useCallback((newKey) => {
    if (!newKey || !layersRef.current[newKey]) return;
    if (visibleKeyRef.current === newKey) return;
    const oldKey = visibleKeyRef.current;
    const newLayer = layersRef.current[newKey];
    // Only keep the visible frame attached to the map.
    // This prevents zoom events from reloading tiles for every cached frame.
    ensureOnMap(newKey);
    // Only show if the layer is currently supposed to be visible
    if (newLayer) newLayer.setOpacity(visibleRef.current ? opacity : 0);
    if (oldKey && layersRef.current[oldKey] && oldKey !== newKey) {
      layersRef.current[oldKey].setOpacity(0);
      removeFromMap(oldKey);
    }
    visibleKeyRef.current = newKey;
    bindVisibleLayerEvents(newKey);
    // If a zoom happened before we had a visible frame, resolve it now.
    resolveZoomWaitForLayer(newLayer);
  }, [opacity, ensureOnMap, removeFromMap, bindVisibleLayerEvents, resolveZoomWaitForLayer]);

  const findBestAvailableFrame = useCallback((targetUnixTime) => {
    const targetKey = getLayerKey(targetUnixTime);
    if (isLayerReady(targetKey)) return targetKey;
    for (let delta = 1; delta <= 6; delta++) {
      const pastKey = getLayerKey(targetUnixTime - (delta * 10800));
      if (isLayerReady(pastKey) && layersRef.current[pastKey]) return pastKey;
      const futureKey = getLayerKey(targetUnixTime + (delta * 10800));
      if (isLayerReady(futureKey) && layersRef.current[futureKey]) return futureKey;
    }
    return visibleKeyRef.current;
  }, [getLayerKey, isLayerReady]);

  const createLayer = useCallback((unixTime) => {
    const key = getLayerKey(unixTime);
    if (layersRef.current[key]) return key;
    const url = buildUrl(unixTime);
    const newLayer = L.tileLayer(url, {
      opacity: 0,
      zIndex: 100,
      keepBuffer: PRELOAD_CONFIG.keepBuffer,
      updateWhenZooming: false,
      updateWhenIdle: true,
    });
    loadStateRef.current[key] = 'loading';
    newLayer.on('load', () => {
      loadStateRef.current[key] = 'loaded';
      if (targetKeyRef.current === key && visibleKeyRef.current !== key) switchVisibleFrame(key);
    });
    // Do not attach preloaded frames to the map.
    // Attaching many tile layers causes zoom to refetch tiles for every cached frame.
    layersRef.current[key] = newLayer;
    return key;
  }, [map, buildUrl, getLayerKey, switchVisibleFrame]);

  // Main effect for frame switching and preloading
  useEffect(() => {
    if (!OPENWEATHERMAP_API_KEY || !map) return;
    // Skip preloading/switching when layer is hidden — tiles stay cached
    if (!visible) return;
    const preloadWindow = getPreloadWindow(isPlaying);
    const currentUnixTime = getCurrentUnixTime();
    const currentKey = getLayerKey(currentUnixTime);
    const isLayerChange = prevLayerRef.current !== null && (prevLayerRef.current !== activeLayer || prevThemeRef.current !== themeKey);
    prevLayerRef.current = activeLayer;
    prevThemeRef.current = themeKey;
    targetKeyRef.current = currentKey;

    if (isLayerChange) {
      if (preloadTimeoutRef.current) { clearTimeout(preloadTimeoutRef.current); preloadTimeoutRef.current = null; }
      isFirstFrameLoadedRef.current = false;
      if (onLayerLoading) onLayerLoading();
      Object.keys(layersRef.current).forEach((key) => {
        const parts = key.split('-');
        if (parts[0] !== activeLayer || parts[1] !== themeKey) {
          removeFromMap(key);
          delete layersRef.current[key];
          delete loadStateRef.current[key];
        }
      });
      visibleKeyRef.current = null;
      const priorityLayer = createLayer(currentUnixTime);
      ensureOnMap(priorityLayer);
      const layer = layersRef.current[priorityLayer];
      if (layer) {
        const onFirstFrameLoad = () => {
          if (!isFirstFrameLoadedRef.current) {
            isFirstFrameLoadedRef.current = true;
            switchVisibleFrame(priorityLayer);
            if (onLayerReady) onLayerReady();
            preloadTimeoutRef.current = setTimeout(() => {
              const forecastStartTime = Math.floor(startDate.getTime() / 1000);
              const forecastEndTime = forecastStartTime + (maxHours * 3600);
              const pw = getPreloadWindow(isPlaying);
              for (let i = 1; i <= pw.framesAhead; i++) {
                const ft = currentUnixTime + (i * 10800);
                if (ft <= forecastEndTime) createLayer(ft);
              }
              for (let i = 1; i <= pw.framesBehind; i++) {
                const pt = currentUnixTime - (i * 10800);
                if (pt >= forecastStartTime) createLayer(pt);
              }
            }, 100);
          }
        };
        if (loadStateRef.current[priorityLayer] === 'loaded') onFirstFrameLoad();
        else layer.once('load', onFirstFrameLoad);
      }
      return;
    }

    if (!isFirstFrameLoadedRef.current && prevLayerRef.current === activeLayer) {
      createLayer(currentUnixTime);
      return;
    }

    // Ensure the target frame is mounted so it can actually load tiles.
    // (Detached tile layers won't fetch tiles, so animation would appear "stuck".)
    createLayer(currentUnixTime);
    ensureOnMap(currentKey);
    const bestFrame = findBestAvailableFrame(currentUnixTime);
    if (bestFrame && bestFrame !== visibleKeyRef.current) switchVisibleFrame(bestFrame);

    const forecastStartTime = Math.floor(startDate.getTime() / 1000);
    const forecastEndTime = forecastStartTime + (maxHours * 3600);
    for (let i = 1; i <= preloadWindow.framesAhead; i++) {
      const ft = currentUnixTime + (i * 10800);
      if (ft <= forecastEndTime) {
        const k = createLayer(ft);
        ensureOnMap(k);
      }
    }
    for (let i = 1; i <= preloadWindow.framesBehind; i++) {
      const pt = currentUnixTime - (i * 10800);
      if (pt >= forecastStartTime) {
        const k = createLayer(pt);
        ensureOnMap(k);
      }
    }

    const maxAge = preloadWindow.maxCachedBehind * 10800;
    const maxFuture = preloadWindow.maxCachedAhead * 10800;
    Object.keys(layersRef.current).forEach((key) => {
      const parts = key.split('-');
      const layerUnixTime = parseInt(parts[parts.length - 1], 10);
      if (parts[0] !== activeLayer || parts[1] !== themeKey) {
        removeFromMap(key);
        delete layersRef.current[key];
        delete loadStateRef.current[key];
        return;
      }
      if (key !== visibleKeyRef.current) {
        if (layerUnixTime < currentUnixTime - maxAge || layerUnixTime > currentUnixTime + maxFuture) {
          removeFromMap(key);
          delete layersRef.current[key];
          delete loadStateRef.current[key];
        }
      }
    });
  }, [map, currentHour, activeLayer, isDark, visible, isPlaying, buildUrl, getCurrentUnixTime, getLayerKey, themeKey, createLayer, findBestAvailableFrame, switchVisibleFrame, startDate, onLayerLoading, onLayerReady, ensureOnMap, removeFromMap]);

  // Zoom/pan: pause playback during zoom, resume once the visible frame's tiles finish loading.
  useEffect(() => {
    if (!map) return;

    const handleZoomStart = () => {
      waitingForTilesAfterZoomRef.current = true;
      if (typeof onTilesLoadingChange === 'function') onTilesLoadingChange(true);
      if (onZoomStart) onZoomStart();
    };

    const handleViewChange = () => {
      // Ensure the visible frame is mounted so it can load tiles at the new zoom.
      if (visibleKeyRef.current) ensureOnMap(visibleKeyRef.current);

      if (waitingForTilesAfterZoomRef.current && visibleKeyRef.current) {
        const currentLayer = layersRef.current[visibleKeyRef.current];
        if (currentLayer) {
          resolveZoomWaitForLayer(currentLayer);
        }
      }
    };

    map.on('zoomstart', handleZoomStart);
    map.on('zoomend', handleViewChange);
    map.on('moveend', handleViewChange);
    return () => { map.off('zoomstart', handleZoomStart); map.off('zoomend', handleViewChange); map.off('moveend', handleViewChange); };
  }, [map, onZoomStart, ensureOnMap, onTilesLoadingChange, resolveZoomWaitForLayer]);

  // (Removed redundant loading reporter effect; zoom lifecycle above is authoritative.)

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (preloadTimeoutRef.current) { clearTimeout(preloadTimeoutRef.current); preloadTimeoutRef.current = null; }
      Object.values(layersRef.current).forEach((layer) => {
        if (map && map.hasLayer(layer)) map.removeLayer(layer);
      });
      const prev = visibleLayerEventsRef.current;
      if (prev.key) {
        const prevLayer = layersRef.current[prev.key];
        if (prevLayer && prev.onLoading && prev.onLoad) {
          prevLayer.off('loading', prev.onLoading);
          prevLayer.off('load', prev.onLoad);
        }
      }
      layersRef.current = {};
      loadStateRef.current = {};
      visibleKeyRef.current = null;
      targetKeyRef.current = null;
    };
  }, [map]);

  return null;
};

export default AnimatedWeatherLayer;
